import { Fragment, useRef, useState } from "react";
import type { FacilityNode } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { FacilityNodeRow } from "./FacilityNodeRow.js";
import { FacilityInlineCreate } from "./FacilityInlineCreate.js";
import { canDrop, isWithin, type FlatRow } from "./facility-tree.js";

/**
 * Accessible timeline tree. Renders the pre-flattened visible rows as a `role="tree"` of `treeitem`s with
 * **roving tabindex** (one Tab stop) + full arrow-key navigation (WAI-ARIA tree pattern):
 *   ↓/↑ move · Home/End jump · → expand-or-into · ← collapse-or-parent · Enter/Space toggle a branch.
 * All expand/favorite STATE lives in the parent page; this owns only focus + key handling.
 */
export interface FacilityTreeProps {
  rows: FlatRow[];
  matchIds: ReadonlySet<number>;
  /** id → entrance delay (ms) for rows just revealed by an expand — drives the top-to-bottom "domino" cascade. Absent
   *  ⇒ no entrance animation (initial render, collapse, reduced-motion). */
  revealDelays: ReadonlyMap<number, number>;
  /** A currently-animating (tagged) row gained focus — end the cascade so focus never rests on an invisible row (a11y). */
  onRevealedRowFocus: () => void;
  favorites: ReadonlySet<number>;
  selectedId: number | null;
  /** Ids the caller can move (best-effort) — drives each row's drag handle + "Move to…". */
  movableIds: ReadonlySet<number>;
  /** The caller is the bypass tier (affects valid drop-target highlighting). */
  bypass: boolean;
  /** Ids the caller has node-level manage on (manage/bypass) — gates each row's kebab Rename / Add child / Manage. */
  canAddIds: ReadonlySet<number>;
  /** Ids the caller has subtree-wide manage on — gates Archive (kebab) + Unarchive (archived view). */
  manageSubtreeIds: ReadonlySet<number>;
  /** Ids the caller can Duplicate (subtree manage AND create-authz on the parent; root = bypass-only) — gates Duplicate. */
  canDuplicateIds: ReadonlySet<number>;
  /** Ids the caller can Insert above (create a SIBLING = a child of the node's parent; ADR-009) — gates Insert above. */
  canInsertAboveIds: ReadonlySet<number>;
  /** Ids that are the TOP of an archived subtree — only these carry the Unarchive action (a descendant of an archived
   *  node can't be restored while its ancestor is archived). */
  archivedRootIds: ReadonlySet<number>;
  /** The Archived filter is active — rows are non-selectable; the only action is Unarchive (on archived roots). */
  archivedMode: boolean;
  /** The row currently in inline-rename mode, or null. */
  renamingId: number | null;
  /** Draft name + pending flag for the row being renamed (owned by the parent page). */
  editName: string;
  savePending: boolean;
  onEditNameChange: (v: string) => void;
  onToggleExpand: (id: number) => void;
  onToggleFavorite: (id: number) => void;
  onSelect: (id: number) => void;
  onMoveTo: (node: FacilityNode) => void;
  onAddChild: (node: FacilityNode) => void;
  onManage: (node: FacilityNode) => void;
  onArchive: (node: FacilityNode) => void;
  onDuplicate: (node: FacilityNode) => void;
  onInsertAbove: (node: FacilityNode) => void;
  onDelete: (node: FacilityNode) => void;
  onUnarchive: (node: FacilityNode) => void;
  onStartRename: (node: FacilityNode) => void;
  onSaveRename: () => void;
  onCancelRename: () => void;
  /** Inline create: the parent an inline "new child" card is open under (parentId null = a realm root), or null when
   *  no create is in progress. Rendered as an indented card in the tree flow — NOT a treeitem, so it stays out of
   *  the roving-tabindex/arrow-key nav (it's a normal Tab stop). */
  createUnder: { parentId: number | null } | null;
  session: AuthSession | null;
  onCreated: (name: string) => void;
  onCancelCreate: () => void;
  /** Move-PICKER mode: the facility being moved. When set, every row renders as a destination picker (star/kebab/"+"/
   *  drag off, moving subtree greyed + badged, invalid targets disabled, valid ones show "↳ Move here"). The browse
   *  action props above are then supplied as harmless no-ops by {@link MoveFacilityDrawer}. */
  moveSource?: FacilityNode | null;
  /** Commit the move to the chosen node as the new parent (picker mode only). */
  onPickDestination?: (node: FacilityNode) => void;
  /** The tree's accessible name (default "Facilities"). The move picker passes a distinct label so it doesn't collide
   *  with the main tree's `view-transition-name` binding (one name per element). */
  ariaLabel?: string;
}

export function FacilityTree({
  rows,
  matchIds,
  revealDelays,
  onRevealedRowFocus,
  favorites,
  selectedId,
  movableIds,
  bypass,
  canAddIds,
  manageSubtreeIds,
  canDuplicateIds,
  canInsertAboveIds,
  archivedRootIds,
  archivedMode,
  renamingId,
  editName,
  savePending,
  onEditNameChange,
  onToggleExpand,
  onToggleFavorite,
  onSelect,
  onMoveTo,
  onAddChild,
  onManage,
  onArchive,
  onDuplicate,
  onInsertAbove,
  onDelete,
  onUnarchive,
  onStartRename,
  onSaveRename,
  onCancelRename,
  createUnder,
  session,
  onCreated,
  onCancelCreate,
  moveSource = null,
  onPickDestination,
  ariaLabel = "Facilities",
}: FacilityTreeProps) {
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const activeIndex = Math.min(focusedIndex, Math.max(0, rows.length - 1));

  const focusRow = (i: number) => {
    const clamped = Math.max(0, Math.min(i, rows.length - 1));
    setFocusedIndex(clamped);
    rowRefs.current[clamped]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const i = activeIndex;
    const row = rows[i];
    if (!row) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focusRow(i + 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        focusRow(i - 1);
        break;
      case "Home":
        e.preventDefault();
        focusRow(0);
        break;
      case "End":
        e.preventDefault();
        focusRow(rows.length - 1);
        break;
      case "ArrowRight":
        e.preventDefault();
        if (row.hasChildren) {
          if (row.expanded) focusRow(i + 1);
          else onToggleExpand(row.node.id);
        }
        break;
      case "ArrowLeft":
        e.preventDefault();
        if (row.hasChildren && row.expanded) {
          onToggleExpand(row.node.id);
        } else {
          for (let j = i - 1; j >= 0; j--) {
            if (rows[j]!.depth < row.depth) {
              focusRow(j);
              break;
            }
          }
        }
        break;
      case "Enter":
        // Activate. In the move PICKER, Enter on a VALID destination row commits the move (the keyboard mirror of
        // "↳ Move here"); an invalid target is a no-op. In browse, Enter = select (WAI-ARIA tree default; expand is
        // Left/Right + Space). Ancestor placeholders (no permission) + the Archived tab are non-selectable.
        e.preventDefault();
        if (moveSource) {
          if (canDrop(moveSource, row.node, bypass)) onPickDestination?.(row.node);
        } else if (!archivedMode && row.node.permission !== null) {
          onSelect(row.node.id);
        }
        break;
      case " ":
        if (row.hasChildren) {
          e.preventDefault();
          onToggleExpand(row.node.id);
        }
        break;
      case "f":
      case "F":
        // Keyboard path to favorite the focused row (the star is also Tab-reachable on the active row). Not in the
        // Archived tab or the move picker (no star there — favoriting an invisible control would confuse).
        if (archivedMode || moveSource) break;
        e.preventDefault();
        onToggleFavorite(row.node.id);
        break;
    }
  };

  return (
    <div role="tree" aria-label={ariaLabel} onKeyDown={onKeyDown} className="flex flex-col">
      {/* New ROOT facility — inline card at the top (depth 0), before any rows. */}
      {createUnder && createUnder.parentId === null ? (
        <FacilityInlineCreate
          parentId={null}
          parentNode={null}
          depth={0}
          session={session}
          onCreated={onCreated}
          onCancel={onCancelCreate}
        />
      ) : null}

      {rows.map((row, index) => {
        const addingHere = createUnder?.parentId === row.node.id;
        return (
          <Fragment key={row.node.id}>
            <FacilityNodeRow
              ref={(el) => {
                rowRefs.current[index] = el;
              }}
              row={row}
              index={index}
              picker={
                moveSource
                  ? {
                      pickable: canDrop(moveSource, row.node, bypass),
                      isSource: row.node.id === moveSource.id,
                      inSubtree: isWithin(row.node.path, moveSource.path) && row.node.id !== moveSource.id,
                      onPickHere: () => onPickDestination?.(row.node),
                    }
                  : null
              }
              revealDelay={revealDelays.get(row.node.id) ?? null}
              onRevealedRowFocus={onRevealedRowFocus}
              isMatch={matchIds.has(row.node.id)}
              isFavorite={favorites.has(row.node.id)}
              selected={row.node.id === selectedId}
              tabbable={index === activeIndex}
              movable={movableIds.has(row.node.id)}
              bypass={bypass}
              canManage={canAddIds.has(row.node.id)}
              canManageSubtree={manageSubtreeIds.has(row.node.id)}
              canDuplicate={canDuplicateIds.has(row.node.id)}
              canInsertAbove={canInsertAboveIds.has(row.node.id)}
              isArchiveRoot={archivedRootIds.has(row.node.id)}
              archivedMode={archivedMode}
              editing={renamingId === row.node.id}
              editName={editName}
              savePending={savePending}
              onEditNameChange={onEditNameChange}
              onToggleExpand={onToggleExpand}
              onToggleFavorite={onToggleFavorite}
              onSelect={onSelect}
              onFocusRow={setFocusedIndex}
              onMoveTo={onMoveTo}
              onAddChild={onAddChild}
              onManage={onManage}
              onArchive={onArchive}
              onDuplicate={onDuplicate}
              onInsertAbove={onInsertAbove}
              onDelete={onDelete}
              onUnarchive={onUnarchive}
              onStartRename={onStartRename}
              onSaveRename={onSaveRename}
              onCancelRename={onCancelRename}
            />
            {/* New CHILD facility — inline card indented under this row (depth + 1). */}
            {addingHere ? (
              <FacilityInlineCreate
                parentId={row.node.id}
                parentNode={row.node}
                depth={row.depth + 1}
                session={session}
                onCreated={onCreated}
                onCancel={onCancelCreate}
              />
            ) : null}
          </Fragment>
        );
      })}
    </div>
  );
}
